<template>
  <v-dialog :value="dialog" @input="$emit('update:dialog', $event)" max-width="700px">
    <v-card v-if="player">
      <v-card-title>
        <div class="d-flex align-center">
          <v-avatar size="48" color="primary" class="white--text mr-3">
            <span class="headline">{{ getInitials(player.player_name) }}</span>
          </v-avatar>
          <div>
            <div class="headline">{{ player.player_name }}</div>
            <div class="caption grey--text">ID: {{ player.player_id }}</div>
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-icon @click="closeDialog" class="cursor-pointer">mdi-close</v-icon>
      </v-card-title>

      <v-card-text>
        <!-- Estado General -->
        <v-row class="mb-4">
          <v-col cols="12" sm="6">
            <v-card class="text-center pa-4 elevation-1">
              <div class="display-1 font-weight-bold orange--text">{{ player.total_warnings }}</div>
              <div class="caption grey--text">Total de Amonestaciones</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6">
            <v-card class="text-center pa-4 elevation-1" :class="{ 'red lighten-5': player.restricted }">
              <div class="display-1 font-weight-bold" :class="`${getStrikesColor(player.strikes)}--text`">
                {{ player.strikes }}/3
              </div>
              <div class="caption grey--text">Strikes Acumulados</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Estado de Restricción -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-alert v-if="player.restricted" type="error" outlined>
              <v-icon left>mdi-lock</v-icon>
              <strong>JUGADOR BLOQUEADO</strong>
              <div class="caption mt-2">Este jugador ha alcanzado el límite de strikes (3) y no puede hacer nuevas reservas.</div>
            </v-alert>
            <v-alert v-else type="success" outlined>
              <v-icon left>mdi-check-circle</v-icon>
              <strong>Jugador Activo</strong>
              <div class="caption mt-2">Puede hacer reservas normalmente. Aún tiene {{ 3 - player.strikes }} strike(s) disponible(s).</div>
            </v-alert>
          </v-col>
        </v-row>

        <v-divider class="mb-4"></v-divider>

        <!-- Historial de Amonestaciones -->
        <v-row>
          <v-col cols="12">
            <div class="subtitle-2 font-weight-bold mb-3">Historial de Amonestaciones</div>
            <v-timeline v-if="player.warnings && player.warnings.length > 0" align-top>
              <v-timeline-item
                v-for="(warning, index) in player.warnings"
                :key="index"
                small
                :color="getCategoryColor(warning.category)"
              >
                <div class="font-weight-bold">{{ warning.warning_type }}</div>
                <div class="caption grey--text">{{ formatDate(warning.date) }} - {{ warning.time }}</div>
                <div class="mt-2 caption">
                  <v-chip x-small class="mr-1">Grupo {{ warning.group }}</v-chip>
                  <v-chip x-small :color="getStrikesColor(warning.strikes)" text-color="white">
                    +{{ warning.strikes }} strikes
                  </v-chip>
                </div>
                <div v-if="warning.notes" class="caption grey--text mt-2">
                  <v-icon x-small>mdi-note</v-icon>
                  {{ warning.notes }}
                </div>
              </v-timeline-item>
            </v-timeline>
            <div v-else class="caption grey--text text-center pa-4">
              No hay amonestaciones registradas
            </div>
          </v-col>
        </v-row>

        <!-- Resumen de strikes por categoría -->
        <v-row class="mt-6">
          <v-col cols="12">
            <div class="subtitle-2 font-weight-bold mb-3">Resumen por Categoría</div>
            <div v-for="category in getStrikesSummary()" :key="category.name" class="mb-3">
              <div class="d-flex justify-space-between align-center mb-1">
                <div class="caption font-weight-bold">{{ category.label }}</div>
                <div class="caption">{{ category.count }} strikes</div>
              </div>
              <v-progress-linear :value="(category.count / 3) * 100" :color="category.color" height="6"></v-progress-linear>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="closeDialog">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'PlayerFileDialog',
  props: {
    dialog: Boolean,
    player: Object,
    warnings: Array
  },
  methods: {
    closeDialog () {
      this.$emit('update:dialog', false)
    },
    getInitials (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    },
    formatDate (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    getCategoryColor (category) {
      const colors = {
        pace: 'orange',
        damage: 'red',
        attire: 'blue',
        conduct: 'red darken-1',
        rules: 'purple',
        warning: 'grey'
      }
      return colors[category] || 'grey'
    },
    getCategoryLabel (category) {
      const labels = {
        pace: 'Ritmo de Juego',
        damage: 'Daño al Campo',
        attire: 'Código de Vestimenta',
        conduct: 'Conducta',
        rules: 'Reglas de Juego',
        warning: 'Advertencia'
      }
      return labels[category] || 'Otro'
    },
    getStrikesColor (strikes) {
      if (strikes >= 2) return 'red'
      if (strikes >= 1) return 'orange'
      return 'green'
    },
    getStrikesSummary () {
      const summary = {
        pace: { count: 0, label: 'Ritmo Lento', color: 'orange' },
        damage: { count: 0, label: 'Daño al Campo', color: 'red' },
        attire: { count: 0, label: 'Vestimenta', color: 'blue' },
        conduct: { count: 0, label: 'Conducta', color: 'red darken-1' },
        rules: { count: 0, label: 'Reglas', color: 'purple' },
        warning: { count: 0, label: 'Advertencias', color: 'grey' }
      }

      if (this.player.warnings && this.player.warnings.length > 0) {
        this.player.warnings.forEach(warning => {
          if (summary[warning.category]) {
            summary[warning.category].count += warning.strikes
          }
        })
      }

      return Object.values(summary).filter(s => s.count > 0)
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
